import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const ROLE_HIERARCHY = ['employee', 'guard', 'location_admin', 'admin_head', 'security_head', 'superadmin'];

const getStoredUser  = () => { try { return JSON.parse(localStorage.getItem('vep_user')); } catch { return null; } };
const getStoredToken = () => localStorage.getItem('vep_token') || null;

export const AuthProvider = ({ children }) => {
  const [user,  setUser]  = useState(getStoredUser);
  const [token, setToken] = useState(getStoredToken);

  // Listen for 401 events dispatched by the axios interceptor.
  // This avoids window.location.href (full reload) — React Router handles the redirect instead.
  useEffect(() => {
    const handleLogout = () => { setToken(null); setUser(null); };
    window.addEventListener('vep:logout', handleLogout);
    return () => window.removeEventListener('vep:logout', handleLogout);
  }, []);

  const _persist = (token, user) => {
    localStorage.setItem('vep_token', token);
    localStorage.setItem('vep_user', JSON.stringify(user));
    setToken(token);
    setUser(user);
  };

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    _persist(data.token, data.user);
    return data.user;
  }, []);

  const loginWithSSO = useCallback(async (idToken) => {
    const { data } = await api.post('/auth/sso', { idToken });
    _persist(data.token, data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('vep_token');
    localStorage.removeItem('vep_user');
    setToken(null);
    setUser(null);
  }, []);

  const hasRole = useCallback((...roles) => {
    const u = getStoredUser();
    return u && roles.includes(u.role);
  }, []);

  const hasMinRole = useCallback((minRole) => {
    const u = getStoredUser();
    if (!u) return false;
    return ROLE_HIERARCHY.indexOf(u.role) >= ROLE_HIERARCHY.indexOf(minRole);
  }, []);

  const canViewAllOffices = useCallback(() => {
    const u = getStoredUser();
    return u && ['superadmin', 'security_head', 'admin_head'].includes(u.role);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      loginWithSSO,
      logout,
      hasRole,
      hasMinRole,
      canViewAllOffices,
      isAuthenticated: !!token,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
